import React, { useEffect, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';

/**
 * ConfirmModal — A reusable, professional confirmation dialog.
 */
export default function ConfirmModal({
    isOpen,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'danger' // 'danger' | 'warning' | 'info'
}) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            document.body.style.overflow = 'hidden';
        } else {
            const timer = setTimeout(() => setIsVisible(false), 200);
            document.body.style.overflow = 'unset';
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (!isOpen && !isVisible) return null;

    const getColors = () => {
        switch (type) {
            case 'danger': return { primary: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', shadow: 'rgba(239, 68, 68, 0.2)' };
            case 'warning': return { primary: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', shadow: 'rgba(245, 158, 11, 0.2)' };
            default: return { primary: '#066af2', gradient: 'linear-gradient(135deg, #066af2, #0550c4)', shadow: 'rgba(6, 106, 242, 0.2)' };
        }
    };

    const colors = getColors();

    const overlayStyle = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.2s ease-out'
    };

    const modalStyle = {
        background: 'white',
        width: '100%',
        maxWidth: '440px',
        borderRadius: '1.25rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden',
        transform: isOpen ? 'scale(1)' : 'scale(0.95)',
        transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex',
        flexDirection: 'column'
    };

    return (
        <div style={overlayStyle} onClick={onCancel}>
            <div style={modalStyle} onClick={e => e.stopPropagation()}>
                {/* Header with icon */}
                <div style={{ padding: '1.5rem 1.5rem 1rem', display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '0.75rem',
                        background: `${colors.primary}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <AlertCircle size={24} color={colors.primary} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '1.25rem',
                            fontWeight: 800,
                            color: '#0f172a',
                            letterSpacing: '-0.02em'
                        }}>
                            {title}
                        </h3>
                        <p style={{
                            margin: '0.5rem 0 0',
                            fontSize: '0.925rem',
                            color: '#64748b',
                            lineHeight: 1.5,
                            fontWeight: 500
                        }}>
                            {message}
                        </p>
                    </div>
                </div>

                {/* Footer Actions */}
                <div style={{
                    padding: '1.25rem 1.5rem',
                    background: '#f8fafc',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.75rem',
                    borderTop: '1px solid #f1f5f9'
                }}>
                    <button
                        onClick={onCancel}
                        style={{
                            padding: '0.625rem 1.25rem',
                            borderRadius: '0.75rem',
                            border: '1px solid #e2e8f0',
                            background: 'white',
                            color: '#475569',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        style={{
                            padding: '0.625rem 1.25rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            background: colors.gradient,
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: `0 4px 12px ${colors.shadow}`,
                            transition: 'all 0.2s'
                        }}
                    >
                        {confirmText}
                    </button>
                </div>

                <button
                    onClick={onCancel}
                    style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'transparent',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s'
                    }}
                >
                    <X size={18} />
                </button>
            </div>
        </div>
    );
}
